import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import intlShape from '../../lib/intlShape.js';
import styles from './domi-car-camera.css';

// Replacing an MJPEG image with a local resource explicitly cancels the
// never-ending network request before React removes the element from the DOM.
const STOPPED_STREAM_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

const messages = defineMessages({
    title: {
        defaultMessage: 'Car camera',
        description: 'Title of the Domi Car camera preview',
        id: 'gui.domiCar.camera.title'
    },
    expand: {
        defaultMessage: 'Enlarge camera view',
        description: 'Label for the button that enlarges the Domi Car camera preview',
        id: 'gui.domiCar.camera.expand'
    },
    collapse: {
        defaultMessage: 'Exit large camera view',
        description: 'Label for the button that shrinks the Domi Car camera preview',
        id: 'gui.domiCar.camera.collapse'
    },
    close: {
        defaultMessage: 'Close camera view',
        description: 'Label for the button that closes the Domi Car camera preview',
        id: 'gui.domiCar.camera.close'
    },
    loadError: {
        defaultMessage: 'Camera view failed to load',
        description: 'Message shown when the Domi Car camera stream cannot be loaded',
        id: 'gui.domiCar.camera.loadError'
    }
});

class DomiCarCamera extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClose',
            'handleDoubleClick',
            'handleStreamError',
            'handleStreamLoad',
            'setStreamElement'
        ]);
        this.state = {
            hasError: false
        };
        this.isStoppingStream = false;
        this.streamElement = null;
    }
    componentDidUpdate (prevProps) {
        if (prevProps.streamUrl !== this.props.streamUrl) {
            this.isStoppingStream = false;
            if (this.state.hasError) this.setState({hasError: false});
        }
    }
    componentWillUnmount () {
        this.stopStream();
    }
    setStreamElement (streamElement) {
        this.streamElement = streamElement;
    }
    stopStream () {
        this.isStoppingStream = true;
        if (this.streamElement) this.streamElement.src = STOPPED_STREAM_IMAGE;
    }
    handleClose () {
        this.stopStream();
        this.props.onClose();
    }
    handleDoubleClick (event) {
        event.stopPropagation();
    }
    handleStreamError () {
        if (this.isStoppingStream) return;
        this.setState({hasError: true});
    }
    handleStreamLoad () {
        if (this.isStoppingStream) return;
        if (this.state.hasError) this.setState({hasError: false});
    }
    render () {
        const {
            intl,
            isExpanded,
            onToggleExpanded,
            streamUrl
        } = this.props;
        const sizeButtonLabel = intl.formatMessage(isExpanded ? messages.collapse : messages.expand);
        const closeButtonLabel = intl.formatMessage(messages.close);

        return (
            <div
                className={classNames(styles.camera, {
                    [styles.expanded]: isExpanded
                })}
                onDoubleClick={this.handleDoubleClick}
            >
                <div className={styles.toolbar}>
                    <div className={styles.title}>
                        <span className={styles.liveDot} />
                        {intl.formatMessage(messages.title)}
                    </div>
                    <div className={styles.controls}>
                        <button
                            aria-label={sizeButtonLabel}
                            className={styles.controlButton}
                            title={sizeButtonLabel}
                            type="button"
                            onClick={onToggleExpanded}
                        >
                            {isExpanded ? '↘' : '⤢'}
                        </button>
                        <button
                            aria-label={closeButtonLabel}
                            className={styles.controlButton}
                            title={closeButtonLabel}
                            type="button"
                            onClick={this.handleClose}
                        >
                            ×
                        </button>
                    </div>
                </div>
                <img
                    alt=""
                    className={styles.stream}
                    draggable={false}
                    ref={this.setStreamElement}
                    src={streamUrl}
                    onError={this.handleStreamError}
                    onLoad={this.handleStreamLoad}
                />
                {this.state.hasError ? (
                    <div className={styles.error}>
                        {intl.formatMessage(messages.loadError)}
                    </div>
                ) : null}
            </div>
        );
    }
}

DomiCarCamera.propTypes = {
    intl: intlShape.isRequired,
    isExpanded: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onToggleExpanded: PropTypes.func.isRequired,
    streamUrl: PropTypes.string.isRequired
};

export default injectIntl(DomiCarCamera);
